import {
  Card,
  EmptyState,
  Measure,
  PageHeader,
  buttonClasses,
  type StatusTone,
} from '@homecare/ui';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  Clock,
  Receipt,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { systemClock, type Clock as WallClock } from '../../lib/clock';
import { greeting } from '../../lib/greeting';
import { useSession } from '../../lib/session';

/**
 * The landing screen.
 *
 * A row of measures, then what needs the reader today. §01's fourth principle
 * is that money and risk are always visible, and a measure with a deadline
 * under it is what makes someone open the product before their email.
 *
 * ## Colour carries meaning, not decoration
 *
 * Each measure's hairline, tile and number take the colour of **what it
 * measures**: brand for the shape of the day, amber once something is queued,
 * red once the oldest thing in that queue is close to a filing deadline,
 * violet for work parked mid-flight. Four gradients chosen for variety would
 * read as a template; four chosen for meaning read as information, and they
 * keep §02's rule that amber and red together stay under roughly a tenth of the
 * screen.
 *
 * ## Honest numbers only
 *
 * None of these measures has data behind it yet: there are no clients, no
 * visits and no exceptions, because those slices are not built. A card showing
 * **0** would be a claim — it says we counted and found none, and we did not
 * count. They show an em dash in neutral grey and one short caption naming what
 * has to exist first: "Once there is a schedule", not a sentence explaining
 * what the label above already says.
 *
 * The **tile and hairline** still carry the measure's tone while the number is
 * pending, because they say what kind of measure it is rather than what it
 * currently reads. An amber number with no count behind it would be a warning
 * about nothing.
 */

/**
 * What colour a review queue is.
 *
 * Amber while it is merely a queue; red once the oldest item is close enough to
 * a filing deadline that waiting costs money. Zero recedes — an empty queue is
 * not an achievement to colour, it is nothing to do.
 *
 * Exported and tested rather than inlined into the render, because "when does
 * this go red" is a rule about the business and not about a stylesheet.
 */
export const DEADLINE_PRESSURE_DAYS = 7;

export function reviewTone(
  count: number,
  daysToOldestDeadline: number | null | undefined,
): StatusTone {
  if (count <= 0) return 'inactive';
  if (daysToOldestDeadline != null && daysToOldestDeadline <= DEADLINE_PRESSURE_DAYS) {
    return 'critical';
  }
  return 'warning';
}

interface MeasureSpec {
  label: string;
  tone: StatusTone | 'brand';
  /**
   * The measure's own mark, as the reference gives each one.
   *
   * Not the tone's status glyph: two of these four would then share a shape,
   * and §05 is explicit that an icon shape never means two things.
   */
  icon: LucideIcon;
  /**
   * What has to exist before there is a number. Never "No data".
   *
   * **One short line.** The slot under the number is a caption, and the copy
   * here was a sentence — "Counts the week's scheduled visits, once there is a
   * schedule." Two lines at this width, which pushed every card from wide and
   * short to nearly square and turned a row of measures into a row of panels.
   *
   * The sentence was good writing in the wrong slot. It said what the measure
   * counts *and* what has to exist first, where the label above has already
   * said the first half: a card titled "Visits this week" does not need a line
   * explaining that it counts the week's visits.
   *
   * Not a tooltip on the icon either. An explanation nobody opens is not an
   * explanation, and it would be keyboard-reachable but never read.
   */
  pending: string;
}

const MEASURES: MeasureSpec[] = [
  {
    label: 'Visits this week',
    tone: 'brand',
    icon: CalendarDays,
    pending: 'Once there is a schedule',
  },
  {
    label: 'Unfilled shifts',
    // Amber is what an unfilled shift looks like while it is still fillable.
    tone: 'warning',
    icon: Clock,
    pending: 'Once there are shifts to fill',
  },
  {
    label: 'Open exceptions',
    // The tone this measure carries once it has a number comes from
    // `reviewTone`. Red is what it looks like when it is blocking money.
    tone: 'critical',
    icon: AlertTriangle,
    pending: 'Once visits are being recorded',
  },
  {
    label: 'Held from billing',
    tone: 'progress',
    icon: Receipt,
    pending: 'Once billing runs',
  },
];

/**
 * Where "Open the board" goes.
 *
 * Typed as `string` rather than left as a literal on purpose. The section
 * routes are generated from `NAVIGATION` at load, so the router's static route
 * union does not contain them — the same reason `NavItem.to` is a `string`.
 * The path is real; TanStack simply cannot see a route it did not receive as a
 * literal.
 */
const SCHEDULE_PATH: string = '/schedule';

export function Dashboard({ clock = systemClock }: { clock?: WallClock } = {}): ReactNode {
  const user = useSession((state) => state.user);
  const permissions = useSession((state) => state.permissions);

  const canOpenBoard = permissions.includes('schedule:read');

  return (
    <div className="grid gap-5">
      <PageHeader
        title={greeting(clock, user?.firstName)}
        meta="Here is where your agency stands right now."
        action={
          canOpenBoard ? (
            <Link to={SCHEDULE_PATH} className={buttonClasses()}>
              Open the board
            </Link>
          ) : undefined
        }
      />

      <section
        aria-label="Where the agency stands"
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {MEASURES.map((measure) => (
          <Measure
            key={measure.label}
            label={measure.label}
            tone={measure.tone}
            icon={<measure.icon className="h-5 w-5" />}
            pending={measure.pending}
          />
        ))}
      </section>

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-ink text-md font-semibold">Today’s visits</h2>
              <p className="text-ink-3 text-xs">The next few on the calendar</p>
            </div>
            {canOpenBoard && (
              <Link
                to={SCHEDULE_PATH}
                className="text-brand hover:text-brand-hover flex flex-none items-center gap-1 text-sm font-semibold"
              >
                Full board
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            )}
          </div>

          <EmptyState
            title="Nothing scheduled today"
            explanation="Visits appear here in the order they start, with the caregiver on each one. There is no schedule to draw from yet."
          />
        </Card>

        <Card>
          <h2 className="text-ink text-md font-semibold">Needs your attention</h2>
          <p className="text-ink-3 text-xs">Sorted by what blocks money first</p>

          <EmptyState
            title="Nothing is waiting on you"
            explanation="Late clock-ins, credentials about to expire, and visits that cannot be billed yet gather here, most costly first."
          />
        </Card>
      </div>
    </div>
  );
}
